import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import { FaTiktok, FaYoutube } from "react-icons/fa";
import { useToast } from "../hooks/use-toast";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    hearAboutUs: "",
    message: "",
    botcheck: "", // honeypot (should remain empty)
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // block bots that fill the honeypot
    if (name === "botcheck") {
      setFormData((prev) => ({ ...prev, botcheck: value }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      interest: value,
    }));
  };

  const handleHearAboutChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      hearAboutUs: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // if the honeypot is filled, silently abort
    if (formData.botcheck) {
      setLoading(false);
      return;
    }

    // your two access keys (free plan ok; this sends twice)
    const accessKeys = [
      "ebfa40e2-5553-480f-a9cf-6ea189ed8f26",
      "f26921e7-7352-4bfd-9d57-7254032742e8",
    ];

    // shared payload — identical shape to the working popup form
    const basePayload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      interest: formData.interest,
      hear_about_us: formData.hearAboutUs,
      message: formData.message,
    };

    try {
      const responses = await Promise.allSettled(
        accessKeys.map((key) =>
          fetch("https://api.web3forms.com/submit", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ access_key: key, ...basePayload }),
          }).then((r) => r.json())
        )
      );

      const successes = responses.filter(
        (res) => res.status === "fulfilled" && res.value?.success
      );

      if (successes.length > 0) {
        toast({
          title: "✅ Message Sent!",
          description:
            "We got your message. You'll receive a response within 24 hours.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          interest: "",
          hearAboutUs: "",
          message: "",
          botcheck: "",
        });
      } else {
        const firstError =
          responses.find((r) => r.status === "fulfilled")?.value?.message ||
          responses.find((r) => r.status === "rejected")?.reason?.message ||
          "Something went wrong. Please try again.";
        toast({
          title: "❌ Submission Failed",
          description: firstError,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "⚠️ Error",
        description: "Network error. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Contact <span className="text-red-600">Us</span>
          </h2>
          <p className="text-gray-300 text-xl">
            Ready to start your boxing journey? Get in touch with us today!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="bg-red-600 border-white/20">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-white">
                  Get In Touch
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="text-black mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Address</h4>
                    <p className="text-gray-300">
                      1950 N Ruby St 
                      <br />
                      Melrose Park, IL 60160
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="text-black mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Phone</h4>
                    
                     <a href="tel:3128855934"
                      className="text-gray-300 hover:text-black transition-colors"
                    >
                      Michelle: (312) 885-5934
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="text-black mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    
                     <a href="mailto:info@claraboxingacademy.com"
                      className="text-gray-300 hover:text-black transition-colors"
                    >
                      info@claraboxingacademy.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="text-black mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Hours</h4>
                    <div className="text-gray-300">
                      <p>Mon-Fri: 5:00 PM - 9:00 PM</p>
                      <p>Saturday: Open on selected Saturdays </p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-white font-semibold mb-3">Follow Us</h4>
                  <div className="flex space-x-4">
                    
                    <a href="https://www.instagram.com/claraboxingacademy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-black transition-colors"
                    >
                      <Instagram size={24} />
                    </a>

                    
                     <a href="https://www.youtube.com/channel/UCfmGec-zhAcD9jnWk80nOLQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-black transition-colors"
                    >
                      <FaYoutube size={24} />
                    </a>

                    
                     <a href="https://www.tiktok.com/@claraboxingacademy?_t=ZP-8zwun79pWHe&_r=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-black transition-colors"
                    >
                      <FaTiktok size={24} />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-red-600 text-white p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-2">
                ✅ Claim Your 3 FREE Trial Days!
              </h3>
              <p className="text-red-100">
                Walk in, call, or email—let&apos;s talk about your goals and how we
                can help you achieve them.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-red-600 border-white/20" id="contactform">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-white">
                Ready to redeem your 3-day free trial?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot - keep it visually hidden */}
                <input
                  type="text"
                  name="botcheck"
                  value={formData.botcheck}
                  onChange={handleInputChange}
                  className="hidden"
                  autoComplete="off"
                  tabIndex={-1}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="name"
                      placeholder="Full Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white"
                    />
                  </div>
                  <div>
                    <Input
                      name="phone"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white"
                    />
                  </div>
                </div>

                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white"
                  />
                </div>

                <div>
                  <Select
                    value={formData.interest}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white">
                      <SelectValue placeholder="What interests you most?" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-gray-700">
                      <SelectItem
                        value="Boxing Class (1-on-1)"
                        className="text-white hover:bg-red-600"
                      >
                        Boxing Class (1-on-1)
                      </SelectItem>
                      <SelectItem
                        value="Mitts Session (12 rounds)"
                        className="text-white hover:bg-red-600"
                      >
                        Mitts Session (12 rounds)
                      </SelectItem>
                      <SelectItem
                        value="CBA Monthly Membership (5 days/week)"
                        className="text-white hover:bg-red-600"
                      >
                        CBA Monthly Membership (5 days/week)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Select
                    value={formData.hearAboutUs}
                    onValueChange={handleHearAboutChange}
                  >
                    <SelectTrigger className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white">
                      <SelectValue placeholder="How did you hear about us?" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-gray-700">
                      <SelectItem
                        value="Facebook"
                        className="text-white hover:bg-red-600"
                      >
                        Facebook
                      </SelectItem>
                      <SelectItem
                        value="Instagram"
                        className="text-white hover:bg-red-600"
                      >
                        Instagram
                      </SelectItem>
                      <SelectItem
                        value="TikTok"
                        className="text-white hover:bg-red-600"
                      >
                        TikTok
                      </SelectItem>
                      <SelectItem
                        value="Referral from Member"
                        className="text-white hover:bg-red-600"
                      >
                        Referral from Member
                      </SelectItem>
                      <SelectItem
                        value="Family/Friend"
                        className="text-white hover:bg-red-600"
                      >
                        Family/Friend
                      </SelectItem>
                      <SelectItem
                        value="Other"
                        className="text-white hover:bg-red-600"
                      >
                        Other
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your goals and any questions you have..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="bg-black border-gray-700 text-white placeholder:text-white focus:border-white min-h-[120px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-black hover:bg-black text-white font-bold py-3 text-lg"
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>

              <p className="text-white text-sm text-center mt-4">
                We&apos;ll respond within 24 hours to get you started on your boxing
                journey!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
